import { zodResolver } from '@hookform/resolvers/zod';
import {
  Settings,
  Shield,
  Database,
  Zap,
  Bell,
  Save,
  RotateCcw,
  Loader2,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

// shadcn-ui components
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import {
  generalSettingsSchema,
  securitySettingsSchema,
  kycSettingsSchema,
  notificationSettingsSchema,
  performanceSettingsSchema,
  defaultGeneralSettings,
  defaultSecuritySettings,
  defaultKycSettings,
  defaultNotificationSettings,
  defaultPerformanceSettings,
} from '@/schemas/systemSettingsValidation';

import ErrorBoundary from '../common/ErrorBoundary';

import SystemSettingsKYC from './SystemSettingsKYC';
import SystemSettingsNotifications from './SystemSettingsNotifications';
import SystemSettingsPerformance from './SystemSettingsPerformance';

const SystemSettings = () => {
  const [activeTab, setActiveTab] = useState('general');

  // Use custom hook for settings management
  const {
    settings,
    loading,
    error,
    updateGeneralSettings,
    updateSecuritySettings,
    updateKYCSettings,
    updateNotificationSettings,
    updatePerformanceSettings,
    resetSettings,
    isUpdating,
  } = useSystemSettings();

  // Form instances for each tab
  const generalForm = useForm({
    resolver: zodResolver(generalSettingsSchema),
    defaultValues: defaultGeneralSettings,
  });

  const securityForm = useForm({
    resolver: zodResolver(securitySettingsSchema),
    defaultValues: defaultSecuritySettings,
  });

  const kycForm = useForm({
    resolver: zodResolver(kycSettingsSchema),
    defaultValues: defaultKycSettings,
  });

  const notificationForm = useForm({
    resolver: zodResolver(notificationSettingsSchema),
    defaultValues: defaultNotificationSettings,
  });

  const performanceForm = useForm({
    resolver: zodResolver(performanceSettingsSchema),
    defaultValues: defaultPerformanceSettings,
  });

  // Load settings from API
  // Update form values when settings are loaded
  useEffect(() => {
    if (settings) {
      if (settings.general && Object.keys(settings.general).length > 0)
        generalForm.reset(settings.general);
      if (settings.security && Object.keys(settings.security).length > 0)
        securityForm.reset(settings.security);
      if (settings.kyc && Object.keys(settings.kyc).length > 0)
        kycForm.reset(settings.kyc);
      if (
        settings.notifications &&
        Object.keys(settings.notifications).length > 0
      )
        notificationForm.reset(settings.notifications);
      if (settings.performance && Object.keys(settings.performance).length > 0)
        performanceForm.reset(settings.performance);
    }
  }, [
    settings,
    generalForm,
    securityForm,
    kycForm,
    notificationForm,
    performanceForm,
  ]);

  // Show error toast when there's an error
  useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to load system settings',
        variant: 'destructive',
      });
    }
  }, [error]);

  const saveSettings = async (category, data) => {
    try {
      // Validate data before saving
      const { validateSystemSettings } =
        await import('@/schemas/systemSettingsValidation');
      const validation = validateSystemSettings({ [category]: data });

      if (!validation.valid) {
        const categoryErrors = validation.errors[category] || [];
        toast({
          title: 'Validation Error',
          description: `Please fix the following errors: ${categoryErrors.join(', ')}`,
          variant: 'destructive',
        });
        return;
      }

      // Use appropriate mutation function based on category
      const mutationMap = {
        general: updateGeneralSettings,
        security: updateSecuritySettings,
        kyc: updateKYCSettings,
        notifications: updateNotificationSettings,
        performance: updatePerformanceSettings,
      };

      const mutation = mutationMap[category];
      if (mutation) {
        await mutation.mutateAsync(data);
        toast({
          title: 'Success',
          description: `${category.charAt(0).toUpperCase() + category.slice(1)} settings saved successfully`,
        });
      } else {
        throw new Error(`Unknown settings category: ${category}`);
      }
    } catch (error) {
      console.error('Failed to save settings:', error);

      // Handle different types of errors
      let errorMessage = error.message;
      if (error.status === 400) {
        errorMessage = 'Invalid data provided. Please check your input.';
      } else if (error.status === 401) {
        errorMessage = 'You are not authorized to perform this action.';
      } else if (error.status === 403) {
        errorMessage = 'Access denied. Insufficient permissions.';
      } else if (error.status === 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (error.name === 'NetworkError') {
        errorMessage = 'Network error. Please check your connection.';
      }

      toast({
        title: 'Error',
        description: `Failed to save ${category} settings: ${errorMessage}`,
        variant: 'destructive',
      });
    }
  };

  const resetToDefaults = async (form, category) => {
    try {
      await resetSettings.mutateAsync(category);
      form.reset();
      toast({
        title: 'Reset',
        description: `${category.charAt(0).toUpperCase() + category.slice(1)} settings reset to defaults`,
      });
    } catch (error) {
      console.error('Failed to reset settings:', error);

      // Handle different types of errors
      let errorMessage = error.message;
      if (error.status === 401) {
        errorMessage = 'You are not authorized to perform this action.';
      } else if (error.status === 403) {
        errorMessage = 'Access denied. Insufficient permissions.';
      } else if (error.status === 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      }

      toast({
        title: 'Error',
        description: `Failed to reset ${category} settings: ${errorMessage}`,
        variant: 'destructive',
      });
    }
  };

  // Form submission handlers
  const onGeneralSubmit = data => saveSettings('general', data);
  const onSecuritySubmit = data => saveSettings('security', data);
  const onKycSubmit = data => saveSettings('kyc', data);
  const onNotificationSubmit = data => saveSettings('notifications', data);
  const onPerformanceSubmit = data => saveSettings('performance', data);

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <Loader2 className='h-8 w-8 animate-spin mx-auto mb-4' />
          <p className='text-muted-foreground'>Loading system settings...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className='container mx-auto p-6 space-y-6'>
        <div className='flex items-center space-x-2'>
          <Settings className='h-6 w-6' />
          <h1 className='text-3xl font-bold'>System Settings</h1>
        </div>

        <p className='text-muted-foreground'>
          Configure system-wide parameters, security settings, and integrations.
        </p>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className='space-y-6'
        >
          <TabsList className='grid w-full grid-cols-5'>
            <TabsTrigger
              value='general'
              className='flex items-center space-x-2'
            >
              <Settings className='h-4 w-4' />
              <span>General</span>
            </TabsTrigger>
            <TabsTrigger
              value='security'
              className='flex items-center space-x-2'
            >
              <Shield className='h-4 w-4' />
              <span>Security</span>
            </TabsTrigger>
            <TabsTrigger value='kyc' className='flex items-center space-x-2'>
              <Database className='h-4 w-4' />
              <span>KYC</span>
            </TabsTrigger>
            <TabsTrigger
              value='notifications'
              className='flex items-center space-x-2'
            >
              <Bell className='h-4 w-4' />
              <span>Notifications</span>
            </TabsTrigger>
            <TabsTrigger
              value='performance'
              className='flex items-center space-x-2'
            >
              <Zap className='h-4 w-4' />
              <span>Performance</span>
            </TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value='general'>
            <Card>
              <CardHeader>
                <CardTitle>General Settings</CardTitle>
                <CardDescription>
                  Basic system configuration and site information.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...generalForm}>
                  <form
                    onSubmit={generalForm.handleSubmit(onGeneralSubmit)}
                    className='space-y-6'
                  >
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                      <FormField
                        control={generalForm.control}
                        name='site_name'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Site Name</FormLabel>
                            <FormControl>
                              <Input placeholder='Enter site name' {...field} />
                            </FormControl>
                            <FormDescription>
                              The name displayed in the application header.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={generalForm.control}
                        name='language'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Default Language</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder='Select language' />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value='en'>English</SelectItem>
                                <SelectItem value='th'>ไทย</SelectItem>
                                <SelectItem value='zh'>中文</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Default language for new users.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={generalForm.control}
                      name='siteDescription'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Site Description</FormLabel>
                          <FormControl>
                            <Input
                              placeholder='Enter site description'
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Brief description of the system purpose.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                      <FormField
                        control={generalForm.control}
                        name='timezone'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Timezone</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder='Select timezone' />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value='Asia/Bangkok'>
                                  Asia/Bangkok (UTC+7)
                                </SelectItem>
                                <SelectItem value='UTC'>UTC (UTC+0)</SelectItem>
                                <SelectItem value='America/New_York'>
                                  America/New_York (UTC-5)
                                </SelectItem>
                                <SelectItem value='Europe/London'>
                                  Europe/London (UTC+0)
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Default timezone for the system.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={generalForm.control}
                        name='sessionTimeout'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Session Timeout (minutes)</FormLabel>
                            <FormControl>
                              <div className='space-y-3'>
                                <Slider
                                  min={5}
                                  max={1440}
                                  step={5}
                                  value={[field.value]}
                                  onValueChange={value =>
                                    field.onChange(value[0])
                                  }
                                  className='w-full'
                                />
                                <div className='text-center text-sm text-muted-foreground'>
                                  {field.value} minutes
                                </div>
                              </div>
                            </FormControl>
                            <FormDescription>
                              User session timeout duration.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Separator />

                    <div className='space-y-4'>
                      <h3 className='text-lg font-medium'>System Toggles</h3>

                      <FormField
                        control={generalForm.control}
                        name='maintenanceMode'
                        render={({ field }) => (
                          <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                            <div className='space-y-0.5'>
                              <FormLabel className='text-base'>
                                Maintenance Mode
                              </FormLabel>
                              <FormDescription>
                                Enable maintenance mode to restrict access to
                                the system.
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={generalForm.control}
                        name='allowRegistration'
                        render={({ field }) => (
                          <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                            <div className='space-y-0.5'>
                              <FormLabel className='text-base'>
                                Allow Registration
                              </FormLabel>
                              <FormDescription>
                                Allow new users to register for accounts.
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className='flex justify-between'>
                      <Button
                        type='button'
                        variant='outline'
                        onClick={() => resetToDefaults(generalForm, 'general')}
                      >
                        <RotateCcw className='h-4 w-4 mr-2' />
                        Reset to Defaults
                      </Button>
                      <Button type='submit' disabled={isUpdating}>
                        {isUpdating ? (
                          <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                        ) : (
                          <Save className='h-4 w-4 mr-2' />
                        )}
                        {isUpdating ? 'Saving...' : 'Save Settings'}
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Settings */}
          <TabsContent value='security'>
            <Card>
              <CardHeader>
                <CardTitle>Security Settings</CardTitle>
                <CardDescription>
                  Configure password policies, authentication, and security
                  measures.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...securityForm}>
                  <form
                    onSubmit={securityForm.handleSubmit(onSecuritySubmit)}
                    className='space-y-6'
                  >
                    <div className='space-y-4'>
                      <h3 className='text-lg font-medium'>Password Policy</h3>

                      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                        <FormField
                          control={securityForm.control}
                          name='passwordMinLength'
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Minimum Password Length</FormLabel>
                              <FormControl>
                                <div className='space-y-3'>
                                  <Slider
                                    min={6}
                                    max={128}
                                    step={1}
                                    value={[field.value]}
                                    onValueChange={value =>
                                      field.onChange(value[0])
                                    }
                                    className='w-full'
                                  />
                                  <div className='text-center text-sm text-muted-foreground'>
                                    {field.value} characters
                                  </div>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={securityForm.control}
                          name='maxLoginAttempts'
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Max Login Attempts</FormLabel>
                              <FormControl>
                                <div className='space-y-3'>
                                  <Slider
                                    min={1}
                                    max={10}
                                    step={1}
                                    value={[field.value]}
                                    onValueChange={value =>
                                      field.onChange(value[0])
                                    }
                                    className='w-full'
                                  />
                                  <div className='text-center text-sm text-muted-foreground'>
                                    {field.value} attempts
                                  </div>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                        <FormField
                          control={securityForm.control}
                          name='passwordRequireUppercase'
                          render={({ field }) => (
                            <FormItem className='flex flex-row items-center justify-between rounded-lg border p-3'>
                              <div className='space-y-0.5'>
                                <FormLabel className='text-sm'>
                                  Uppercase
                                </FormLabel>
                                <FormDescription className='text-xs'>
                                  Require A-Z
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={securityForm.control}
                          name='passwordRequireLowercase'
                          render={({ field }) => (
                            <FormItem className='flex flex-row items-center justify-between rounded-lg border p-3'>
                              <div className='space-y-0.5'>
                                <FormLabel className='text-sm'>
                                  Lowercase
                                </FormLabel>
                                <FormDescription className='text-xs'>
                                  Require a-z
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={securityForm.control}
                          name='passwordRequireNumbers'
                          render={({ field }) => (
                            <FormItem className='flex flex-row items-center justify-between rounded-lg border p-3'>
                              <div className='space-y-0.5'>
                                <FormLabel className='text-sm'>
                                  Numbers
                                </FormLabel>
                                <FormDescription className='text-xs'>
                                  Require 0-9
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={securityForm.control}
                          name='passwordRequireSpecialChars'
                          render={({ field }) => (
                            <FormItem className='flex flex-row items-center justify-between rounded-lg border p-3'>
                              <div className='space-y-0.5'>
                                <FormLabel className='text-sm'>
                                  Special
                                </FormLabel>
                                <FormDescription className='text-xs'>
                                  Require !@#$
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <Separator />

                    <div className='space-y-4'>
                      <h3 className='text-lg font-medium'>
                        Authentication Settings
                      </h3>

                      <FormField
                        control={securityForm.control}
                        name='lockoutDuration'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Account Lockout Duration (minutes)
                            </FormLabel>
                            <FormControl>
                              <div className='space-y-3'>
                                <Slider
                                  min={1}
                                  max={1440}
                                  step={1}
                                  value={[field.value]}
                                  onValueChange={value =>
                                    field.onChange(value[0])
                                  }
                                  className='w-full'
                                />
                                <div className='text-center text-sm text-muted-foreground'>
                                  {field.value} minutes
                                </div>
                              </div>
                            </FormControl>
                            <FormDescription>
                              Duration to lock accounts after max failed
                              attempts.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={securityForm.control}
                        name='twoFactorRequired'
                        render={({ field }) => (
                          <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                            <div className='space-y-0.5'>
                              <FormLabel className='text-base'>
                                Require Two-Factor Authentication
                              </FormLabel>
                              <FormDescription>
                                Require all users to enable 2FA for their
                                accounts.
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={securityForm.control}
                        name='sessionSecure'
                        render={({ field }) => (
                          <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                            <div className='space-y-0.5'>
                              <FormLabel className='text-base'>
                                Secure Sessions
                              </FormLabel>
                              <FormDescription>
                                Use secure cookies and HTTPS-only sessions.
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className='flex justify-between'>
                      <Button
                        type='button'
                        variant='outline'
                        onClick={() =>
                          resetToDefaults(securityForm, 'security')
                        }
                      >
                        <RotateCcw className='h-4 w-4 mr-2' />
                        Reset to Defaults
                      </Button>
                      <Button type='submit' disabled={isUpdating}>
                        {isUpdating ? (
                          <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                        ) : (
                          <Save className='h-4 w-4 mr-2' />
                        )}
                        {isUpdating ? 'Saving...' : 'Save Settings'}
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* KYC Settings */}
          <SystemSettingsKYC
            form={kycForm}
            onSubmit={onKycSubmit}
            saving={isUpdating}
            resetToDefaults={resetToDefaults}
          />

          {/* Notifications Settings */}
          <SystemSettingsNotifications
            form={notificationForm}
            onSubmit={onNotificationSubmit}
            saving={isUpdating}
            resetToDefaults={resetToDefaults}
          />

          {/* Performance Settings */}
          <SystemSettingsPerformance
            form={performanceForm}
            onSubmit={onPerformanceSubmit}
            saving={isUpdating}
            resetToDefaults={resetToDefaults}
          />
        </Tabs>
      </div>
    </ErrorBoundary>
  );
};

export default SystemSettings;
